/**
 * Pure, side-effect-free rotation-history reducer (#265 / 07 §2.2).
 *
 * The `rotating` facilitation mode lets any campaign member hold the
 * facilitator badge for one session. This module provides:
 *   - `RotationState` — the JSON-serialisable slice that travels in
 *     `Model` and is written to the op log via `config.set`.
 *   - `designateFacilitator` — owner sets the facilitator at session-start
 *     setup (or mid-session if the owner holds the badge).
 *   - `handOffFacilitator` — the CURRENT facilitator passes the badge to
 *     another member mid-session (guard-checked).
 *   - `countByMember` — derive per-member session counts for the fairness
 *     display ("Mira 3, Jules 2, Sam 4").
 *   - `nextFairFacilitator` — suggest the member with the fewest sessions
 *     (skip the current badge-holder when others are available).
 *
 * Every function is pure (no I/O, no mutation). The state lives in
 * `Model.rotationState`; writes dispatch `config.set session_facilitator` +
 * `config.set rotation_history` ops over the WS sync client.
 *
 * JSON-serialisable Records only (llui Model constraint — no Maps/Sets/Dates).
 */

import type { RotationHistoryEntry } from '@dl/shared';

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

/**
 * The ephemeral+persistent rotation slice on `Model`. The persistent part
 * (facilitator + history) lives in the campaign config bucket (op log); the
 * UI-only part (modal open state) is purely ephemeral.
 */
export interface RotationState {
  /** The user_id of the current session's facilitator, or null. */
  session_facilitator: string | null;
  /** Full ordered history of facilitator assignments (oldest-first). */
  rotation_history: RotationHistoryEntry[];
}

/** A freshly-initialised rotation state with no history and no facilitator. */
export const EMPTY_ROTATION_STATE: RotationState = {
  session_facilitator: null,
  rotation_history: [],
};

// ---------------------------------------------------------------------------
// handOffFacilitator result
// ---------------------------------------------------------------------------

/** Success + failure shapes for the hand-off guard. */
export type HandOffResult =
  | { ok: true; state: RotationState }
  | { ok: false; reason: 'not_current_facilitator' | 'no_active_facilitator' | 'same_facilitator' };

// ---------------------------------------------------------------------------
// Pure reducers
// ---------------------------------------------------------------------------

/**
 * Designate a new facilitator (owner action). Appends to `rotation_history`
 * and sets `session_facilitator`. No guard — only the owner calls this.
 */
export function designateFacilitator(
  state: RotationState,
  userId: string,
  assignedAt: string,
): RotationState {
  const entry: RotationHistoryEntry = { user_id: userId, assigned_at: assignedAt };
  return {
    session_facilitator: userId,
    rotation_history: [...state.rotation_history, entry],
  };
}

/**
 * Hand off the facilitator badge from `callerId` to `nextUserId`. Guards:
 *   - `callerId` must be the current `session_facilitator`.
 *   - There must be an active facilitator (non-null).
 *   - `nextUserId` must differ from `callerId` (no self-pass).
 * Returns a `HandOffResult`; callers must handle the `ok: false` branch.
 */
export function handOffFacilitator(
  state: RotationState,
  callerId: string,
  nextUserId: string,
  assignedAt: string,
): HandOffResult {
  if (state.session_facilitator === null) {
    return { ok: false, reason: 'no_active_facilitator' };
  }
  if (state.session_facilitator !== callerId) {
    return { ok: false, reason: 'not_current_facilitator' };
  }
  if (callerId === nextUserId) {
    return { ok: false, reason: 'same_facilitator' };
  }
  return { ok: true, state: designateFacilitator(state, nextUserId, assignedAt) };
}

/**
 * Build a per-member session count from the rotation history. Returns a
 * `Record<userId, count>` for every id in `memberIds`; ids not appearing in
 * the history get count 0. Unknown ids in the history are silently ignored
 * (defence-in-depth: a stale history may reference departed members).
 */
export function countByMember(
  history: RotationHistoryEntry[],
  memberIds: readonly string[],
): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const id of memberIds) {
    counts[id] = 0;
  }
  for (const entry of history) {
    if (Object.prototype.hasOwnProperty.call(counts, entry.user_id)) {
      counts[entry.user_id] = (counts[entry.user_id] ?? 0) + 1;
    }
  }
  return counts;
}

/**
 * Suggest the next fair facilitator from `memberIds`. Selection rule:
 *   1. Prefer members with fewer sessions (minimum count wins).
 *   2. Among ties, prefer members who have NOT gone most recently
 *      (i.e. whose last appearance in history is earlier, or absent).
 *   3. Prefer members OTHER than the current facilitator if any are
 *      available at the minimum count.
 *   4. If only one member exists, always return them.
 *   5. Returns `null` when `memberIds` is empty.
 *
 * Pure — callers decide whether to auto-designate or show the suggestion.
 */
export function nextFairFacilitator(
  state: RotationState,
  memberIds: readonly string[],
): string | null {
  if (memberIds.length === 0) return null;
  if (memberIds.length === 1) return memberIds[0] ?? null;

  const counts = countByMember(state.rotation_history, memberIds);

  // Compute last-appearance index for recency tiebreak (lower = less recent = preferred).
  const lastAppearance: Record<string, number> = {};
  for (const id of memberIds) {
    lastAppearance[id] = -1; // never appeared
  }
  for (let i = 0; i < state.rotation_history.length; i++) {
    const uid = state.rotation_history[i]?.user_id;
    if (uid !== undefined && Object.prototype.hasOwnProperty.call(lastAppearance, uid)) {
      lastAppearance[uid] = i;
    }
  }

  const minCount = Math.min(...memberIds.map((id) => counts[id] ?? 0));

  // Candidates at the minimum count, excluding the current facilitator unless
  // they are the only option at that count.
  const atMin = memberIds.filter((id) => (counts[id] ?? 0) === minCount);
  const nonCurrent = atMin.filter((id) => id !== state.session_facilitator);
  const candidates = nonCurrent.length > 0 ? nonCurrent : atMin;

  // Stable sort: lower lastAppearance index wins (least recently facilitated).
  // Among equal recency, preserve `memberIds` order.
  const sorted = [...candidates].sort((a, b) => {
    const la = lastAppearance[a] ?? -1;
    const lb = lastAppearance[b] ?? -1;
    return la - lb;
  });

  return sorted[0] ?? null;
}
